import { ReactNode, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'
import { TOKENS } from '../tokens'
import { useOverlay } from './useOverlay'
import { LAYERS, OverlayLayer, useOverlayLayer } from './overlayLayer'

interface ConfirmDialogProps {
  open: boolean
  title: ReactNode
  body?: ReactNode
  confirmLabel: string
  cancelLabel?: string
  tone?: 'danger' | 'default'
  pending?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Asks before an action that moves money, changes someone's access or is hard
 * to undo. Say who and how much in the title or body, and name the action on
 * the confirm button ("Mark paid", not "OK").
 *
 * - Focus starts on Cancel and goes back to whatever opened the dialog when it
 *   closes.
 * - While `pending` (the request is running) it can't be dismissed and both
 *   buttons are disabled. The confirm button also ignores a second click that
 *   lands before `pending` shows, so a double click sends one request.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId()
  const bodyId = useId()
  const confirmedRef = useRef(false)
  const zIndex = useOverlayLayer(LAYERS.dialog)

  // Escape cancels, except while the request runs; Tab stays inside; focus
  // starts on Cancel and goes back to whatever opened the dialog (useOverlay).
  const panelRef = useOverlay<HTMLDivElement>({
    open,
    onClose: onCancel,
    busy: pending,
    initialFocus: (panel) => panel.querySelector<HTMLElement>('[data-confirm-cancel]'),
  })

  // After a confirm click, ignore more clicks until the page has re-rendered
  // without a request in progress (it failed, finished, or the dialog closed).
  useEffect(() => {
    if (!open || !pending) confirmedRef.current = false
  })

  if (!open) return null

  // On document.body so it covers the header and isn't clipped by a drawer,
  // and above whatever overlay asked.
  return createPortal(
    <OverlayLayer zIndex={zIndex}>
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex }}>
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          aria-hidden
          data-testid="confirm-dialog-backdrop"
          // Pressing the backdrop mustn't take focus out of the dialog.
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            // detail > 1 is the second click of the double click that opened the dialog.
            if (!pending && e.detail <= 1) onCancel()
          }}
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={body ? bodyId : undefined}
          tabIndex={-1}
          className={`relative w-full max-w-md rounded-2xl p-5 space-y-4 shadow-xl outline-none ${TOKENS.bgCard} ${TOKENS.border}`}
        >
          <h2 id={titleId} className="font-display font-bold text-lg text-foreground">
            {title}
          </h2>
          {body ? (
            <div id={bodyId} className={`text-sm space-y-2 ${TOKENS.textSecondary}`}>
              {body}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" data-confirm-cancel="" disabled={pending} onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button
              type="button"
              variant={tone === 'danger' ? 'danger' : 'primary'}
              loading={pending}
              onClick={(e) => {
                // Ignore a click-through from the double click that opened the
                // dialog, and repeat clicks while the request is on its way.
                if (e.detail > 1 || confirmedRef.current) return
                confirmedRef.current = true
                onConfirm()
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </OverlayLayer>,
    document.body,
  )
}
